import React, { createContext, useContext, useState, useEffect } from 'react';

type UserType = {
  id: string;
  email: string;
} | null;

type AuthContextType = {
  user: UserType;
  loading: boolean;
  sendEmailCode: (email: string) => Promise<void>;
  verifyEmailCode: (email: string, code: string) => Promise<boolean>;
  logout: () => void;
};

// Mock user database - in a real app, this would be in a backend
const USERS_STORAGE_KEY = 'travelbuddy_users';
const CURRENT_USER_KEY = 'travelbuddy_current_user';

// Create the auth context
const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserType>(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem(CURRENT_USER_KEY);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Error parsing stored user:", error);
      }
    }
    setLoading(false);
  }, []);

  // Helper function to get users from local storage
  const getUsers = () => {
    const usersJson = localStorage.getItem(USERS_STORAGE_KEY);
    let users: Record<string, { id: string; email: string }> = {};
    
    if (usersJson) {
      try {
        users = JSON.parse(usersJson);
      } catch (error) {
        console.error("Error parsing users:", error);
      }
    }
    
    return users;
  };

  // Send verification code to email
  const sendEmailCode = async (email: string) => {
    // This would normally call an API to send an email with a code
    // For demo purposes, we'll generate a code and store it in localStorage
    
    const code = Math.floor(100000 + Math.random() * 900000).toString(); // 6-digit code
    const codeExpiryTime = Date.now() + 15 * 60 * 1000; // 15 minutes expiry
    
    // Store the code with the email
    localStorage.setItem(`email_code_${email}`, JSON.stringify({
      code,
      expiry: codeExpiryTime
    }));
    
    console.log(`Verification code for ${email}: ${code}`);
    
    // In a real app, this is where you would send an API request
    // to your backend to trigger an email containing the code
    return Promise.resolve();
  };

  // Verify the email code
  const verifyEmailCode = async (email: string, inputCode: string): Promise<boolean> => {
    const storedCodeData = localStorage.getItem(`email_code_${email}`);
    
    if (!storedCodeData) {
      console.error("No verification code found for this email");
      return false;
    }
    
    try {
      const { code, expiry } = JSON.parse(storedCodeData);
      
      // Check if code has expired
      if (Date.now() > expiry) {
        console.error("Verification code has expired");
        return false;
      }
      
      // Check if the code matches
      if (inputCode !== code) {
        console.error("Invalid verification code");
        return false;
      }
      
      // Code is valid, check if user exists
      const users = getUsers();
      let userId: string;
      
      if (users[email]) {
        // Existing user, just log them in
        userId = users[email].id;
      } else {
        // New user, create account
        userId = crypto.randomUUID();
        const updatedUsers = {
          ...users,
          [email]: { id: userId, email }
        };
        localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(updatedUsers));
      }
      
      // Set the authenticated user
      const authenticatedUser = { id: userId, email };
      setUser(authenticatedUser);
      localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(authenticatedUser));
      
      // Clean up the verification code
      localStorage.removeItem(`email_code_${email}`);
      
      return true;
    } catch (error) {
      console.error("Error verifying code:", error);
      return false;
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem(CURRENT_USER_KEY);
  };

  return (
    <AuthContext.Provider value={{ user, loading, sendEmailCode, verifyEmailCode, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
